import React, { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { CalendarRange, Globe, MapPin, Info, User, Target } from "lucide-react";

/**
 * PricingMatrixV2 (Msg 722 revision) — Editor de preços com comissão-alvo.
 *
 * • Por CADA coluna de categoria: input de comissão-alvo (€ ou %) no cabeçalho.
 * • Por CADA célula (base/season × categoria): input Cliente paga.
 *   Debaixo: Ganha (real = cliente − admin), Fica DT+M (= admin/preço ditours.pt).
 *   VERMELHO se cliente < admin (DT+M abaixo do preço mínimo DiTours).
 *   AVISO se ganha < comissão-alvo (não atinge o que quer receber).
 *
 * Props:
 *   basePrices: {[cat_key]: price}  — preço admin (DiTours) por categoria
 *   seasons, categories, zoneKey, overrides, onChange
 *   testIdPrefix: string
 */
export default function PricingMatrixV2({
  basePrices = {},
  seasons = [],
  categories = [],
  zoneKey,
  overrides = {},
  onChange,
  testIdPrefix = "pm",
}) {
  const [commissionMode, setCommissionMode] = useState("eur"); // eur | pct

  const applicableSeasons = useMemo(() => {
    return seasons.filter((s) => s.scope === "global" || (s.scope === "zone" && s.zone_key === zoneKey));
  }, [seasons, zoneKey]);

  const bpVal = overrides.base_prices || {};
  const spVal = overrides.season_prices || {};
  const commissions = overrides.commissions || {};

  const setBase = (col, value) => {
    const bp = { ...(overrides.base_prices || {}) };
    if (value === "" || value === null || value === undefined) delete bp[col];
    else bp[col] = Number(value);
    onChange({ ...overrides, base_prices: bp });
  };

  const setSeasonPrice = (seasonId, col, value) => {
    const sp = { ...(overrides.season_prices || {}) };
    const entry = { ...(sp[seasonId] || {}) };
    if (value === "" || value === null || value === undefined) delete entry[col];
    else entry[col] = Number(value);
    if (Object.keys(entry).length === 0) delete sp[seasonId];
    else sp[seasonId] = entry;
    onChange({ ...overrides, season_prices: sp });
  };

  const setCommission = (col, value) => {
    const c = { ...(overrides.commissions || {}) };
    if (value === "" || value === null || value === undefined) delete c[col];
    else c[col] = Number(value);
    onChange({ ...overrides, commissions: c });
  };

  // targetEur retorna a comissão-alvo em EUR (converte % se necessário)
  const targetEur = (col, admin) => {
    const raw = commissions[col];
    if (raw == null || raw === "") return null;
    const v = Number(raw);
    if (isNaN(v)) return null;
    if (commissionMode === "pct") return admin * (v / 100);
    return v;
  };

  const renderCell = (col, currentVal, onSet, testId) => {
    const adminPrice = Number(basePrices[col]) || 0;
    const hasAdmin = adminPrice > 0;
    const target = targetEur(col, adminPrice);
    const suggestedClient = hasAdmin ? adminPrice + (target || 0) : (target || 0);
    const isEmpty = currentVal == null || currentVal === "";
    const client = isEmpty ? 0 : Number(currentVal);
    const ganha = client - adminPrice;
    const belowMinimum = hasAdmin && client > 0 && client < adminPrice - 0.01;
    const missTarget = target != null && target > 0 && !belowMinimum && !isEmpty && ganha < target - 0.01;

    return (
      <td key={col} className="p-2 align-top">
        <div className="flex flex-col gap-1">
          {/* Preço cliente — grande no topo */}
          <Input
            type="number" min="0" step="0.5"
            max="99999"
            value={currentVal ?? ""}
            placeholder={hasAdmin ? `€${suggestedClient.toFixed(0)}` : "definir"}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === "") return onSet(col, "");
              const n = Number(raw);
              if (isNaN(n)) return;
              onSet(col, Math.min(99999, Math.max(0, n)));
            }}
            className={`w-full h-10 text-right !text-base font-bold ${belowMinimum ? "!border-red-500 bg-red-50 !text-red-700" : "text-[#0a0f1e]"} ${!hasAdmin && isEmpty ? "border-amber-300 bg-amber-50/40" : ""}`}
            data-testid={testId}
          />
          {/* Breakdown compacto por baixo */}
          <div className="text-[10px] leading-tight font-mono truncate">
            {isEmpty ? (
              !hasAdmin ? (
                <div className="text-[9px] text-amber-700 uppercase tracking-wider truncate">sem base admin</div>
              ) : target && target > 0 ? (
                <div className="text-[9px] text-slate-400 uppercase tracking-wider truncate">sugere €{suggestedClient.toFixed(0)}</div>
              ) : (
                <div className="text-[9px] text-slate-400 uppercase tracking-wider truncate">min €{adminPrice.toFixed(0)}</div>
              )
            ) : (
              <>
                <div className={`truncate font-bold ${belowMinimum ? "text-red-600" : "text-emerald-700"}`}>
                  {belowMinimum ? "⚠" : "✓"} Comissão €{ganha.toFixed(0)}
                </div>
                {hasAdmin && (
                  <div className={`truncate ${belowMinimum ? "text-red-600 font-bold" : "text-slate-500"}`}>
                    DT+M €{Math.min(client, adminPrice).toFixed(0)}{belowMinimum ? ` <${adminPrice.toFixed(0)}` : ""}
                  </div>
                )}
                {missTarget && target != null && (
                  <div className="text-[9px] text-amber-700 font-semibold truncate">abaixo do alvo €{target.toFixed(0)}</div>
                )}
              </>
            )}
          </div>
        </div>
      </td>
    );
  };

  return (
    <div className="border border-slate-200 bg-white">
      <div className="p-3 border-b border-slate-200 bg-slate-50/50 flex items-start gap-2 text-[11px] text-slate-600">
        <Info className="w-3.5 h-3.5 text-[#f59e0b] mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <strong>Como funciona:</strong> defina a <strong>comissão-alvo</strong> (o que quer ganhar) por categoria e o <strong>preço cliente</strong> por célula.
          O sistema mostra em tempo real quanto ganha e quanto fica para DiTours+Motorista.
          <br /><strong className="text-red-600">Aviso vermelho:</strong> se o preço cliente for inferior ao preço mínimo DiTours (base admin), significa que fica menos do que o valor no site ditours.pt.
        </div>
        <div className="flex gap-1 items-center shrink-0">
          <span className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">Comissão:</span>
          {[{ k: "eur", l: "€" }, { k: "pct", l: "%" }].map((m) => (
            <button
              key={m.k}
              type="button"
              onClick={() => setCommissionMode(m.k)}
              data-testid={`${testIdPrefix}-comm-mode-${m.k}`}
              className={`px-2 py-0.5 text-[10px] font-bold border transition-colors ${commissionMode === m.k ? "border-[#f59e0b] text-[#f59e0b] bg-[#f59e0b]/10" : "border-slate-300 text-slate-500 hover:border-slate-400"}`}
            >
              {m.l}
            </button>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm table-fixed" style={{ minWidth: `${260 + categories.length * 150}px` }}>
          <colgroup>
            <col style={{ width: "260px" }} />
            {categories.map((c) => (
              <col key={c.key} style={{ width: `${Math.max(150, Math.floor(900 / categories.length))}px` }} />
            ))}
          </colgroup>
          <thead>
            <tr className="border-b-2 border-slate-200 bg-slate-50">
              <th className="p-2 text-left text-[10px] uppercase tracking-widest font-bold text-slate-500">Temporada</th>
              {categories.map((c) => {
                const admin = Number(basePrices[c.key]) || 0;
                return (
                  <th key={c.key} className="p-2 text-right text-[10px] uppercase tracking-widest font-bold text-slate-500">
                    <div className="mb-1">{c.label}</div>
                    {admin > 0 && (
                      <div className="text-[9px] text-slate-400 font-mono normal-case mb-1">
                        Ref DiTours €{admin.toFixed(0)}
                      </div>
                    )}
                  </th>
                );
              })}
            </tr>
            {/* Linha de comissão-alvo por coluna */}
            <tr className="border-b border-slate-300 bg-[#f59e0b]/5">
              <th className="p-2 text-right text-[10px] uppercase tracking-widest font-bold text-[#f59e0b] flex items-center justify-end gap-1">
                <Target className="w-3 h-3" /> Comissão-alvo:
              </th>
              {categories.map((c) => (
                <th key={c.key} className="p-2 align-top">
                  <div className="flex items-stretch border border-slate-300 focus-within:border-[#f59e0b] focus-within:ring-2 focus-within:ring-[#f59e0b]/20 transition-colors bg-white overflow-hidden">
                    <span className="flex items-center justify-center px-2 bg-slate-50 text-slate-500 text-xs font-mono border-r border-slate-300 shrink-0">
                      {commissionMode === "eur" ? "€" : "%"}
                    </span>
                    <input
                      type="number" min="0" step="0.5"
                      max={commissionMode === "pct" ? "100" : "9999"}
                      value={commissions[c.key] ?? ""}
                      placeholder="0"
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === "") return setCommission(c.key, "");
                        const n = Number(raw);
                        if (isNaN(n)) return;
                        const capped = commissionMode === "pct" ? Math.min(100, Math.max(0, n)) : Math.min(9999, Math.max(0, n));
                        setCommission(c.key, capped);
                      }}
                      className="flex-1 min-w-0 px-2 h-8 text-right !text-sm outline-none bg-white"
                      style={{ MozAppearance: "textfield" }}
                      data-testid={`${testIdPrefix}-commission-${c.key}`}
                    />
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-slate-100" data-testid={`${testIdPrefix}-base-row`}>
              <td className="p-2 align-top">
                <div className="text-sm font-semibold text-[#0a0f1e] flex items-center gap-1">
                  <Globe className="w-3.5 h-3.5 text-slate-400" /> Base (fora de temporada)
                </div>
                <div className="text-[10px] text-slate-500">Quando nenhuma temporada cobre a data</div>
              </td>
              {categories.map((c) => renderCell(c.key, bpVal[c.key], setBase, `${testIdPrefix}-base-${c.key}`))}
            </tr>
            {applicableSeasons.length === 0 && (
              <tr>
                <td colSpan={categories.length + 1} className="p-4 text-center text-xs text-slate-400 italic">
                  Sem temporadas aplicáveis a esta zona.
                </td>
              </tr>
            )}
            {applicableSeasons.map((s) => (
              <tr key={s.id} className={`border-b border-slate-100 ${s.owner_type === "partner" ? "bg-[#fff8ec]/40" : ""}`} data-testid={`${testIdPrefix}-season-${s.id}`}>
                <td className="p-2 align-top">
                  <div className="text-sm font-semibold text-[#0a0f1e] flex items-center gap-1.5">
                    <CalendarRange className="w-3.5 h-3.5 text-[#f59e0b]" /> {s.name}
                    {s.owner_type === "partner" && (
                      <span className="inline-flex items-center gap-0.5 text-[8px] uppercase tracking-widest font-bold text-[#f59e0b] bg-[#f59e0b]/10 px-1.5 py-0.5 border border-[#f59e0b]/40">
                        <User className="w-2.5 h-2.5" /> Minha
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-slate-500 flex items-center gap-1">
                    {s.scope === "global" ? <Globe className="w-2.5 h-2.5" /> : <MapPin className="w-2.5 h-2.5" />}
                    <span>{s.scope === "global" ? "Global" : `Zona: ${s.zone_key}`}</span>
                    <span>· prio {s.priority}</span>
                  </div>
                  <div className="text-[10px] text-slate-400 font-mono">
                    {(s.date_ranges || []).map((r, i) => (
                      <div key={i}>{String(r.start_day).padStart(2, "0")}/{String(r.start_month).padStart(2, "0")} → {String(r.end_day).padStart(2, "0")}/{String(r.end_month).padStart(2, "0")}</div>
                    ))}
                  </div>
                </td>
                {categories.map((c) => {
                  const entry = spVal[s.id] || {};
                  return renderCell(c.key, entry[c.key], (col, val) => setSeasonPrice(s.id, col, val), `${testIdPrefix}-season-${s.id}-${c.key}`);
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
